import { generatePath, Outlet } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { ICONS_MAP } from 'constants/icons';
import { ROUTES } from 'routes/config/constants';
import { IRoutesConfigItem } from 'routes/config/index';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';

const Psp = appLazy(() => import('routes/pages/AuthorizedStack/PspManagement/Psp'));
const PspView = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/Psp/View'),
);

const Terminals = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/Terminals'),
);
const Terminal = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/Terminals/Terminal'),
);

const TerminalsLinks = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/TerminalsLinks'),
);
const TerminalLink = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/TerminalLink/View'),
);
const CreateTerminalLink = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/TerminalLink/Create'
    ),
);

const TerminalLinkSettlementConfigs = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/TerminalLink/View/SettlementConfigsForm'
    ),
);
const TerminalLinkRequiredParameters = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/TerminalLink/View/RequiredParamsForm'
    ),
);
const GroupEditTerminalLinkRP = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/GroupEditRequiredParams'
    ),
);
const GroupCreateTerminalLinkSC = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/GroupCreateSettlementConfigs'
    ),
);
const CopyTerminalLink = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/TerminalsLinks/Copy'),
);
const TerminalsLinksLimits = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/TerminalLinksLimits'),
);
const CopyTLLimits = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/PspManagement/TerminalLinksLimits/CopyLimits'),
);

const TerminalLinksParameters = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/TerminalLinksParameters'),
);

const TerminalLinksGroups = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/TerminalLinksGroups'),
);
const TerminalLinksGroup = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalLinksGroups/TerminalLinkGroup'
    ),
);
const AddTerminalLinks = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/PspManagement/TerminalLinksGroups/TerminalLinkGroup/AddTLForm'
    ),
);
const AuditLogs = appLazy(() => import('routes/pages/AuthorizedStack/AuditLogs'));

export const PSP_MANAGEMENT_CHILDREN: IRoutesConfigItem[] = [
  {
    breadcrumb: 'psp.title',
    children: [
      {
        component: Psp,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_PSPS],
      },
      {
        breadcrumb: (params) => {
          return params?.id || 'psp.subTitle';
        },
        children: [
          {
            component: PspView,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_PSPS],
          },
          {
            breadcrumb: (params) => {
              return params?.targetId || '';
            },
            children: [
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS
                  .PATH,
                permissions: [PERMISSIONS.CAN_VIEW_PSPS, PERMISSIONS.VIEW_AUDIT_LOGS],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS
                  .PATH,
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS
                  .SUB_PATH.CHILD,
                permissions: [PERMISSIONS.CAN_VIEW_PSPS, PERMISSIONS.VIEW_AUDIT_LOGS],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS
                  .SUB_PATH.CHILD,
              },
            ],
            component: Outlet,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS
              .PATH,
            permissions: [PERMISSIONS.CAN_VIEW_PSPS, PERMISSIONS.VIEW_AUDIT_LOGS],
            url: (params) => {
              const id = params?.id;
              const { PARAMS, PATH } = ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW;
              return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
            },
          },
        ],
        component: Outlet,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_PSPS],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } = ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.SUB_PATH.VIEW;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isPrivate: true,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.PSP />,
    path: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_PSPS],
    testId: 'navbarPsp',
    url: ROUTES.PSP_MANAGEMENT.SUB_PATH.PSP.PATH,
  },
  {
    breadcrumb: 'terminals.title',
    children: [
      {
        component: Terminals,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS],
      },
      {
        breadcrumb: (params) => {
          return params?.id || 'terminal.title';
        },
        children: [
          {
            component: Terminal,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS],
          },
          {
            breadcrumb: (params) => {
              return params?.targetId || '';
            },
            children: [
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.SUB_PATH
                  .AUDIT_LOGS.PATH,
                permissions: [
                  PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.SUB_PATH
                  .AUDIT_LOGS.PATH,
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.SUB_PATH
                  .AUDIT_LOGS.SUB_PATH.CHILD,
                permissions: [
                  PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.SUB_PATH
                  .AUDIT_LOGS.SUB_PATH.CHILD,
              },
            ],
            component: Outlet,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.SUB_PATH
              .AUDIT_LOGS.PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS,
              PERMISSIONS.VIEW_AUDIT_LOGS,
            ],
            url: (params) => {
              const id = params?.id;
              const { PARAMS, PATH } =
                ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL;
              return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
            },
          },
        ],
        component: Outlet,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL.PATH,
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.SUB_PATH.TERMINAL;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isPrivate: true,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.Terminals />,
    path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS],
    testId: 'navbarTerminals',
    url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS.PATH,
  },
  {
    breadcrumb: 'terminalsLinks.title',
    children: [
      {
        component: TerminalsLinks,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS],
      },
      {
        breadcrumb: 'terminalLink.create',
        component: CreateTerminalLink,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_TERMINAL_LINK],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.CREATE,
      },
      {
        component: GroupEditTerminalLinkRP,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.GROUP_EDIT_RP,
        permissions: [PERMISSIONS.CAN_GROUP_EDIT_TERMINAL_LINK_REQUIRED_PARAMETERS],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.GROUP_EDIT_RP,
      },
      {
        component: GroupCreateTerminalLinkSC,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.GROUP_CREATE_SC,
        permissions: [PERMISSIONS.CAN_GROUP_CREATE_TERMINAL_LINK_SETTLEMENT_CONFIGS],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.GROUP_CREATE_SC,
      },
      {
        breadcrumb: 'terminalsLinks.bulkCopy',
        component: CopyTerminalLink,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.COPY,
        permissions: [PERMISSIONS.CAN_COPY_TERMINAL_LINKS],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.COPY,
      },
      {
        component: TerminalLinkRequiredParameters,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
          .REQUIRED_PARAMETERS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINK_REQUIRED_PARAMETERS],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
          .REQUIRED_PARAMETERS.PATH,
      },
      {
        component: TerminalLinkSettlementConfigs,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
          .SETTLEMENT_CONFIGURATIONS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINK_SETTLEMENT_CONFIGS],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
          .SETTLEMENT_CONFIGURATIONS.PATH,
      },
      {
        breadcrumb: (params) => {
          return params?.id || 'terminalLink.title';
        },
        children: [
          {
            component: TerminalLink,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS],
          },
          {
            breadcrumb: (params) => {
              return params?.targetId || '';
            },
            children: [
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
                  .AUDIT_LOGS.PATH,
                permissions: [
                  PERMISSIONS.CAN_VIEW_TERMINAL_LINKS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
                  .AUDIT_LOGS.PATH,
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
                  .AUDIT_LOGS.SUB_PATH.CHILD,
                permissions: [
                  PERMISSIONS.CAN_VIEW_TERMINAL_LINKS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK
                  .AUDIT_LOGS.SUB_PATH.CHILD,
              },
            ],
            component: Outlet,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK.AUDIT_LOGS
              .PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_TERMINAL_LINKS,
              PERMISSIONS.VIEW_AUDIT_LOGS,
            ],
            url: (params) => {
              const id = params?.id;
              const { PARAMS, PATH } =
                ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK;
              return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
            },
          },
        ],
        component: Outlet,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK.PATH,
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.TERMINAL_LINK;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.TerminalLinks />,
    path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS],
    testId: 'navbarTerminalsLinks',
    url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS.PATH,
  },
  {
    breadcrumb: 'terminalLinksLimits.title',
    children: [
      {
        component: TerminalsLinksLimits,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS_LIMITS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_LIMITS],
      },
      {
        breadcrumb: 'terminalLinksLimits.copy.title',
        component: CopyTLLimits,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS_LIMITS.COPY,
        permissions: [PERMISSIONS.CAN_COPY_TERMINAL_LINK_LIMITS],
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS_LIMITS.COPY,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.TerminalLinksLimits />,
    path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS_LIMITS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_LIMITS],
    testId: 'navbarTerminalsLinksLimits',
    url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINALS_LINKS_LIMITS.PATH,
  },
  {
    breadcrumb: 'terminalLinksParameters.title',
    children: [
      {
        component: TerminalLinksParameters,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_PARAMETERS.PATH,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.TerminalLinksParameters />,
    path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_PARAMETERS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_PARAMETERS],
    testId: 'navbarTerminalsLinksParameters',
    url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_PARAMETERS.PATH,
  },
  {
    breadcrumb: 'terminalLinksGroups.title',
    children: [
      {
        component: TerminalLinksGroups,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS],
      },
      {
        breadcrumb: (params) => {
          return params?.id || 'terminalLinkGroup.title';
        },
        children: [
          {
            component: TerminalLinksGroup,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS
              .TERMINAL_LINKS_GROUP.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS],
          },
          {
            breadcrumb: (params) => {
              return params?.targetId || '';
            },
            children: [
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS
                  .TERMINAL_LINKS_GROUP.SUB_PATH.AUDIT_LOGS.PATH,
                permissions: [
                  PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS
                  .TERMINAL_LINKS_GROUP.SUB_PATH.AUDIT_LOGS.PATH,
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS
                  .TERMINAL_LINKS_GROUP.SUB_PATH.AUDIT_LOGS.SUB_PATH.CHILD,
                permissions: [
                  PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS
                  .TERMINAL_LINKS_GROUP.SUB_PATH.AUDIT_LOGS.SUB_PATH.CHILD,
              },
            ],
            component: Outlet,
            path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS
              .TERMINAL_LINKS_GROUP.SUB_PATH.AUDIT_LOGS.PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS,
              PERMISSIONS.VIEW_AUDIT_LOGS,
            ],
            url: (params) => {
              const id = params?.id;
              const { PARAMS, PATH } =
                ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.TERMINAL_LINKS_GROUP;
              return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
            },
          },
        ],
        component: Outlet,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.TERMINAL_LINKS_GROUP
          .PATH,
        permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.TERMINAL_LINKS_GROUP;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        component: AddTerminalLinks,
        path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.TERMINAL_LINKS_GROUP
          .SUB_PATH.TERMINAL_LINK.PATH,
        url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.TERMINAL_LINKS_GROUP
          .SUB_PATH.TERMINAL_LINK.PATH,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.TerminalLinksGroups />,
    path: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS],
    testId: 'navbarTerminalsLinksGroups',
    url: ROUTES.PSP_MANAGEMENT.SUB_PATH.TERMINAL_LINKS_GROUPS.PATH,
  },
];
